// src/components/scan/ReportModal.tsx
import React from 'react';

export interface ReportModalProps {
  domain: string;
  reportEmail: string;
  setReportEmail: (value: string) => void;
  privacyAccepted: boolean;
  setPrivacyAccepted: (value: boolean) => void;
  onClose: () => void;
  onSubmit: () => void;
  t: (key: string, options?: any) => string;
}

const ReportModal: React.FC<ReportModalProps> = ({
  domain,
  reportEmail,
  setReportEmail,
  privacyAccepted,
  setPrivacyAccepted,
  onClose,
  onSubmit,
  t,
}) => {
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reportEmail);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isEmailValid) onSubmit();
  };

  const sendReportButtonClasses = `
    w-full sm:w-auto
    bg-gradient-to-r from-yellow-500 to-yellow-700
    hover:from-yellow-600 hover:to-yellow-800
    text-white font-medium
    rounded-lg transition-all
    disabled:opacity-50 disabled:cursor-not-allowed
    px-4 py-2 sm:px-6 sm:py-2
  `;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50">
      <div
        className="
          bg-gray-900 border-2 border-blue-700 rounded-xl
          w-full max-w-xs sm:max-w-3xl m-4
          p-4 sm:p-10
          shadow-xl text-white
        "
      >
        <h2 className="text-xl sm:text-3xl font-bold mb-2">
          {t('scan.reportModal.title')}
        </h2>
        <p className="text-sm sm:text-lg text-gray-300 mb-6">
          {t('scan.reportModal.subtitle')}{' '}
          <span className="font-semibold text-gray-400">{domain}</span>
        </p>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-sm sm:text-base font-medium mb-1 text-gray-300">
                {t('scan.reportModal.emailLabel')}
              </label>
              <input
                type="email"
                value={reportEmail}
                onChange={(e) => setReportEmail(e.target.value)}
                placeholder={t('scan.reportModal.emailPlaceholder')}
                className={`
                  w-full bg-gray-800 border
                  ${reportEmail && !isEmailValid ? 'border-red-500' : 'border-gray-700'}
                  rounded-lg
                  px-3 py-2 sm:px-4 sm:py-2
                  focus:outline-none focus:border-blue-500
                  text-white text-sm sm:text-base
                `}
                required
              />
              {reportEmail && !isEmailValid && (
                <p className="text-red-500 text-xs sm:text-sm mt-1">
                  {t('scan.reportModal.invalidEmail')}
                </p>
              )}
            </div>

            {/* Privacy */}
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="privacy"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                className="mt-1 accent-yellow-500 w-4 h-4 sm:w-5 sm:h-5"
                required
              />
              <label
                htmlFor="privacy"
                className="text-xs sm:text-sm text-gray-300 flex-1"
              >
                {t('scan.reportModal.privacyConsent')}
              </label>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
              <button
                type="submit"
                disabled={!privacyAccepted || !reportEmail || !isEmailValid}
                className={sendReportButtonClasses}
              >
                {t('scan.reportModal.sendReport')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="
                  w-full sm:w-auto
                  bg-gray-700 hover:bg-gray-600
                  text-white font-medium
                  rounded-lg
                  px-4 py-2 sm:px-6 sm:py-2
                  transition-all
                "
              >
                {t('scan.reportModal.close')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportModal;
